import React from "react";
import { Stack, Typography } from "@mui/material";
import dayjs from "dayjs";

import { HubCardStack } from "../../components";
import { StyledMonthGrid, StyledMonthCard, StyledDayCell } from "./styles";

const MONTHS = Array.from({ length: 12 }, (_, i) => i);
const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const TODAY = dayjs().format("YYYY-MM-DD");
const CURRENT_MONTH = dayjs().month();

const isWeekend = (date) => {
  const day = dayjs(date).day();
  return day === 0 || day === 6;
};

const MonthCalendar = ({ month, year, closedSet, canEdit, onDateClick }) => {
  const firstDay = dayjs().year(year).month(month).startOf("month");
  const daysInMonth = firstDay.daysInMonth();
  const startDay = firstDay.day();
  const isCurrentMonth = month === CURRENT_MONTH;

  const cells = Array.from({ length: startDay }, () => null).concat(
    Array.from({ length: daysInMonth }, (_, i) => firstDay.add(i, "day")),
  );

  return (
    <StyledMonthCard>
      <Typography
        variant="boldCaption"
        sx={{
          pb: "4px",
          ...(isCurrentMonth && { color: "hubColors.mainFocus" }),
        }}
      >
        {firstDay.format("MMMM")}
      </Typography>
      <Stack sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {WEEKDAY_LABELS.map((d) => (
          <Typography
            key={d}
            variant="caption"
            sx={{
              fontSize: 8,
              textAlign: "center",
              opacity: 0.5,
              fontWeight: 600,
            }}
          >
            {d}
          </Typography>
        ))}
        {cells.map((date, idx) => {
          if (!date) return <span key={`e-${idx}`} />;
          const key = date.format("YYYY-MM-DD");
          const weekend = isWeekend(date);
          const isClosed = closedSet.has(key);
          const isToday = key === TODAY;

          return (
            <StyledDayCell
              key={key}
              isToday={isToday}
              isClosed={isClosed}
              isWeekend={weekend}
              onClick={() => !weekend && canEdit && onDateClick(date)}
            >
              {date.date()}
            </StyledDayCell>
          );
        })}
      </Stack>
    </StyledMonthCard>
  );
};

const CalendarClosedDays = ({ closedSet, canEdit, onDateClick }) => {
  const year = dayjs().year();

  return (
    <HubCardStack height="100%" gap="15px">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack>
          <Typography variant="boldCaptionL">Closed Days</Typography>
          <Typography variant="caption">
            Click a weekday to mark it exempt.
          </Typography>
        </Stack>
        <Stack direction="row" gap="10px" alignItems="center">
          <Stack direction="row" gap="4px" alignItems="center">
            <Stack
              sx={{
                width: 12,
                height: 3,
                borderRadius: 1,
                backgroundColor: "hubColors.greyMid",
              }}
            />
            <Typography variant="caption" sx={{ fontSize: 9 }}>
              Closed
            </Typography>
          </Stack>
          <Stack direction="row" gap="4px" alignItems="center">
            <Stack
              sx={{
                width: 12,
                height: 3,
                borderRadius: 1,
                backgroundColor: "hubColors.redMain",
              }}
            />
            <Typography variant="caption" sx={{ fontSize: 9 }}>
              Exempt
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <StyledMonthGrid>
        {MONTHS.map((month) => (
          <MonthCalendar
            key={month}
            month={month}
            year={year}
            closedSet={closedSet}
            canEdit={canEdit}
            onDateClick={onDateClick}
          />
        ))}
      </StyledMonthGrid>
    </HubCardStack>
  );
};

export default CalendarClosedDays;
